import React, { useState } from "react";

import { Formik, Form } from "formik";
import { motion } from "framer-motion";
import { RightMinimalArrow } from "icons";

import archivalDataApi from "apis/archivalData";
import { Button, Typography } from "clearsense-ui/components";
import { Checkbox, Input } from "clearsense-ui/components/formik";
import { Error, Search } from "components/Common/clearsense-ui/icons";
import { useFeaturesState } from "contexts/features";

import {
  INITIAL_FORM_VALUES,
  FIELDS_VALLEY_CHILDREN,
  FIELDS_NATIONAL_RAD,
  FORM_VALIDATION,
} from "./constants";
import ResultModal from "./ResultModal";
import Results from "./Results";
import { stringToSnakeCase } from "./utils";

const NestedPDFSearch = () => {
  const [result, setResult] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [error, setError] = useState(false);
  const [isOpen, setIsOpen] = useState({
    Program: true,
    IDTS: true,
    Intervention: true,
    Goal: true,
  });
  const features = useFeaturesState();
  const isValleyChildren =
    features?.all?.["extract_text_parser"]?.[0] === "valley_children";

  const fields = isValleyChildren
    ? FIELDS_VALLEY_CHILDREN
    : FIELDS_NATIONAL_RAD.map(header => ({ header }));
  const handleSearch = async values => {
    const selectedFields = Object.keys(values)
      .filter(value => values[value] === true)
      .map(value => stringToSnakeCase(value));

    if (!selectedFields.length) {
      setError(true);
      setShowResult(false);
      setResult([]);
    } else {
      setError(false);
      try {
        const { data } = await archivalDataApi.advancedSearch(
          values.term,
          selectedFields
        );
        setResult(data.matches);
      } catch (error) {
        logger.error(error);
      } finally {
        setShowResult(true);
      }
    }
  };

  const handleSelectAll = setFieldValue =>
    isValleyChildren
      ? fields.map(field =>
          field.nestedHeaders.map(nestedHeader =>
            setFieldValue(`${field.header} ${nestedHeader}`, true)
          )
        )
      : fields.map(field => setFieldValue(`${field.header}`, true));

  const handleClearAll = setFieldValue =>
    isValleyChildren
      ? fields.map(field =>
          field.nestedHeaders.map(nestedHeader =>
            setFieldValue(`${field.header} ${nestedHeader}`, false)
          )
        )
      : fields.map(field => setFieldValue(`${field.header}`, false));

  return (
    <div className="flex flex-col ">
      <Formik
        initialValues={INITIAL_FORM_VALUES}
        validationSchema={FORM_VALIDATION}
        onSubmit={values => handleSearch(values)}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form>
            <div className="mx-8">
              <Typography style="h3" className="mt-8" type="semibold">
                Advanced PDF Search
              </Typography>
              <div className="flex flex-col my-8 space-y-4">
                <div className="flex-col space-y-2">
                  <Typography style="h4">Search Term</Typography>
                  <div className="flex items-start space-x-4">
                    <Input
                      placeholder="Search PDF"
                      name="term"
                      data-cy="term-input"
                    />
                    <Button
                      label="Search"
                      type="submit"
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      icon={Search}
                      data-cy="submit-button"
                    />
                  </div>
                </div>
                <div className="flex h-screen" style={{ maxHeight: "75vh" }}>
                  <div className="flex flex-col flex-shrink-0 px-5 py-3 space-y-2 overflow-y-auto border border-current w-80 dark:bg-shades-darkest cs-ui-scrollbar">
                    <Typography style="h4" className="dark:text-shade-light">
                      List of headings
                    </Typography>
                    <Typography
                      style="medium"
                      className="text-gray-600 dark:text-gray-400"
                    >
                      Select the headings under which the search term should be
                      searched.
                    </Typography>
                    {!!error && (
                      <div className="flex items-center space-x-1 text-red-700">
                        <Error size={14} />
                        <Typography style="medium" data-cy="heading-error">
                          Should select atleast one heading.
                        </Typography>
                      </div>
                    )}
                    <div>
                      <Button
                        label="Select all"
                        className="pl-0 underline"
                        style="text"
                        onClick={() => handleSelectAll(setFieldValue)}
                      />
                      <Button
                        label="Clear all"
                        className="underline"
                        style="text"
                        onClick={() => handleClearAll(setFieldValue)}
                      />
                    </div>
                    {fields.map(field => (
                      <div
                        key={field.header}
                        className="mb-2 dark:text-shade-light"
                      >
                        {field.nestedHeaders ? (
                          <>
                            <div
                              className="flex items-center justify-between"
                              onClick={() =>
                                setIsOpen({
                                  ...isOpen,
                                  [field.header]: !isOpen[field.header],
                                })
                              }
                            >
                              <Typography style="h4">{field.header}</Typography>
                              <motion.div
                                variants={{
                                  open: { rotate: 90 },
                                  collapsed: { rotate: 0 },
                                }}
                                animate={
                                  isOpen[field.header] ? "open" : "collapsed"
                                }
                                transition={{ duration: 0.3 }}
                                className="flex-grow-0 cs-ui-accordion__item-toggle-icon"
                              >
                                <RightMinimalArrow size={16} color="#68737D" />
                              </motion.div>
                            </div>
                            <motion.div
                              variants={{
                                open: { opacity: 1 },
                                collapsed: { opacity: 0, scale: 0, height: 0 },
                              }}
                              animate={
                                isOpen[field.header] ? "open" : "collapsed"
                              }
                              transition={{ duration: 0.3 }}
                            >
                              <div className="mt-2">
                                {field?.nestedHeaders?.map(
                                  (nestedHeader, nestedIndex) => (
                                    <Checkbox
                                      label={nestedHeader}
                                      name={`${field.header} ${nestedHeader}`}
                                      key={`${nestedHeader}-${nestedIndex}`}
                                      className="mb-2 ml-2"
                                    />
                                  )
                                )}
                              </div>
                            </motion.div>
                          </>
                        ) : (
                          <Typography
                            style="h4"
                            className="flex items-center justify-between"
                          >
                            {field.header}
                            <Checkbox
                              name={`${field.header}`}
                              key={`${field.header}`}
                              className="flex-grow-0"
                            />
                          </Typography>
                        )}
                      </div>
                    ))}
                  </div>
                  {showResult && (
                    <Results
                      result={result}
                      setShowModal={setShowModal}
                      setSelectedFile={setSelectedFile}
                      isValleyChildren={isValleyChildren}
                    />
                  )}
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      {showModal && (
        <ResultModal
          showModal={showModal}
          onClose={() => setShowModal(false)}
          selectedFile={selectedFile}
          isValleyChildren={isValleyChildren}
        />
      )}
    </div>
  );
};

export default NestedPDFSearch;
